import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchSocialLinks, updateSocialLinks, createSocialLinks } from "../../../../redux/socialSlice";
import { AppDispatch, RootState } from "../../../../redux/store";
import { motion, AnimatePresence } from "framer-motion";
import {
  Facebook, Instagram, Twitter, Youtube,
  Smartphone, Play, CheckCircle, Loader2, Globe,
} from "lucide-react";

/* ─────────────────────────────────────────────────────────
   FONTS
───────────────────────────────────────────────────────── */
const FONT_CSS = `
  @import url('https://fonts.googleapis.com/css2?family=Cormorant+Garamond:ital,wght@0,300;0,400;0,500;0,600&family=Montserrat:wght@300;400;500;600;700&display=swap');
`;

/* ─────────────────────────────────────────────────────────
   THEME
───────────────────────────────────────────────────────── */
const C = {
  paper:        "#ffffff",
  surface:      "#f7f7f7",
  ink:          "#0a0a0a",
  inkSoft:      "#2a2a2a",
  inkMid:       "#6b6b6b",
  inkLight:     "#a3a3a3",
  border:       "#e0e0e0",
  success:      "#1a7a3a",
  successBg:    "#f0faf4",
  successBorder:"#b8dfc8",
  serif:        "'Cormorant Garamond', Georgia, serif",
  sans:         "'Montserrat', sans-serif",
};

/* ─────────────────────────────────────────────────────────
   FIELD ROW
───────────────────────────────────────────────────────── */
const Field: React.FC<{
  label:       string;
  placeholder: string;
  value:       string;
  onChange:    (v: string) => void;
  icon:        React.ReactNode;
  hint?:       string;
}> = ({ label, placeholder, value, onChange, icon, hint }) => {
  const [focused, setFocused] = useState(false);
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:7 }}>
      <label style={{
        fontSize:9, letterSpacing:"0.22em", color:C.inkMid,
        fontFamily:C.sans, fontWeight:600,
      }}>
        {label}
      </label>
      <div style={{
        display:"flex", alignItems:"center",
        border:`1px solid ${focused ? C.ink : C.border}`,
        background:C.paper, transition:"border-color 0.2s",
      }}>
        {/* Icon slab */}
        <div style={{
          width:44, height:44, display:"flex", alignItems:"center",
          justifyContent:"center", flexShrink:0,
          borderRight:`1px solid ${focused ? C.ink : C.border}`,
          color:focused ? C.ink : C.inkLight,
          transition:"border-color 0.2s, color 0.2s",
          background:C.surface,
        }}>
          {icon}
        </div>
        <input
          type="text"
          placeholder={placeholder}
          value={value}
          onChange={e => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          style={{
            flex:1, background:"transparent", border:"none", outline:"none",
            padding:"11px 14px", fontSize:12, color:C.ink,
            fontFamily:"'Courier New', monospace", letterSpacing:"0.02em",
          }}
        />
        {/* Green dot when filled */}
        {value.trim() && (
          <div style={{
            width:6, height:6, borderRadius:"50%",
            background:C.success, marginRight:14, flexShrink:0,
          }}/>
        )}
      </div>
      {hint && (
        <p style={{ fontSize:9, color:C.inkLight, letterSpacing:"0.04em", marginTop:2 }}>
          {hint}
        </p>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════
   MAIN
════════════════════════════════════════════════════════════ */
const SocialHandles: React.FC = () => {
  const dispatch           = useDispatch<AppDispatch>();
  const { social, loading } = useSelector((state: RootState) => state.social);

  /* ── all original state ── */
  const [facebook,    setFacebook]    = useState("");
  const [instagram,   setInstagram]   = useState("");
  const [twitter,     setTwitter]     = useState("");
  const [youtube,     setYoutube]     = useState("");
  const [appStoreId,  setAppStoreId]  = useState("");
  const [playStoreId, setPlayStoreId] = useState("");
  const [saving,      setSaving]      = useState(false);
  const [saved,       setSaved]       = useState(false);

  useEffect(() => { dispatch(fetchSocialLinks()); }, [dispatch]);

  useEffect(() => {
    if (social) {
      setFacebook(social.facebook     || "");
      setInstagram(social.instagram   || "");
      setTwitter(social.twitter       || "");
      setYoutube(social.youtube       || "");
      setAppStoreId(social.appStoreId || "");
      setPlayStoreId(social.playStoreId || "");
    }
  }, [social]);

  /* ── original handleSave logic ── */
  const handleSave = async () => {
    setSaving(true); setSaved(false);
    const data = { facebook, instagram, twitter, youtube, appStoreId, playStoreId };
    if (social?.id) {
      await dispatch(updateSocialLinks({ id: social.id, data }));
    } else {
      await dispatch(createSocialLinks(data));
    }
    setTimeout(() => { setSaving(false); setSaved(true); setTimeout(() => setSaved(false), 2500); }, 1200);
  };

  /* Filled count for the checklist badge */
  const filledCount = [facebook,instagram,twitter,youtube,appStoreId,playStoreId].filter(v => v.trim()).length;

  return (
    <div style={{ background:C.paper, fontFamily:C.sans, color:C.ink }}>
      <style>{FONT_CSS + SH_CSS}</style>

      {/* ── TITLE STRIP ── */}
      <div style={S.titleStrip}>
        <div style={{ height:2, background:C.ink, marginBottom:16 }}/>
        <div style={{ display:"flex", alignItems:"flex-end", justifyContent:"space-between", flexWrap:"wrap", gap:8, marginBottom:16 }}>
          <div>
            <p style={S.eye}>CONTENT MANAGEMENT</p>
            <h1 style={S.title}>Social & App Links</h1>
          </div>
          <span style={S.countPill}>
            {filledCount} / 6 configured
          </span>
        </div>
        <div style={{ height:1, background:C.border }}/>
      </div>

      <div style={S.body}>
        <div className="sh-layout">

          {/* ═══════════════════════════════
              LEFT — FORM
          ═══════════════════════════════ */}
          <div style={{ display:"flex", flexDirection:"column", gap:24 }}>

            {/* Social section */}
            <div>
              <div style={S.sectionHead}>
                <div style={{ width:24, height:2, background:C.ink, flexShrink:0 }}/>
                <h2 style={S.sectionTitle}>Social Media</h2>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <Field label="FACEBOOK"  placeholder="https://facebook.com/yourpage"     value={facebook}  onChange={setFacebook}  icon={<Facebook  size={15} strokeWidth={1.5}/>}/>
                <Field label="INSTAGRAM" placeholder="https://instagram.com/yourhandle"  value={instagram} onChange={setInstagram} icon={<Instagram size={15} strokeWidth={1.5}/>}/>
                <Field label="TWITTER / X" placeholder="https://twitter.com/yourhandle" value={twitter}   onChange={setTwitter}   icon={<Twitter   size={15} strokeWidth={1.5}/>}/>
                <Field label="YOUTUBE"   placeholder="https://youtube.com/yourchannel"   value={youtube}   onChange={setYoutube}   icon={<Youtube   size={15} strokeWidth={1.5}/>}/>
              </div>
            </div>

            {/* App store section */}
            <div>
              <div style={S.sectionHead}>
                <div style={{ width:24, height:2, background:C.ink, flexShrink:0 }}/>
                <h2 style={S.sectionTitle}>App Stores</h2>
              </div>
              <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
                <Field
                  label="APPLE APP STORE"
                  placeholder="id123456789"
                  value={appStoreId}
                  onChange={setAppStoreId}
                  icon={<Smartphone size={15} strokeWidth={1.5}/>}
                  hint="Apple App Store ID — e.g. id123456789"
                />
                <Field
                  label="GOOGLE PLAY STORE"
                  placeholder="com.app.name"
                  value={playStoreId}
                  onChange={setPlayStoreId}
                  icon={<Play size={15} strokeWidth={1.5}/>}
                  hint="Google Play Store package ID — e.g. com.yourapp.name"
                />
              </div>
            </div>

            {/* Save button */}
            <motion.button
              whileTap={{ scale:0.97 } as any}
              onClick={handleSave}
              disabled={saving || loading}
              style={{
                ...S.saveBtn,
                opacity: saving || loading ? 0.65 : 1,
                cursor:  saving || loading ? "not-allowed" : "pointer",
                background: saved ? C.successBg : C.ink,
                color:      saved ? C.success   : "#fff",
                border:     saved ? `1px solid ${C.successBorder}` : "none",
              }}
            >
              {saving || loading
                ? <><Loader2 size={14} className="sh-spin" style={{ marginRight:8 }}/> SAVING…</>
                : saved
                  ? <><CheckCircle size={14} style={{ marginRight:8 }}/> SAVED</>
                  : "SAVE CHANGES"
              }
            </motion.button>

          </div>

          {/* ═══════════════════════════════
              RIGHT — STATUS SIDEBAR
          ═══════════════════════════════ */}
          <div style={S.sidebar}>

            {/* Link status */}
            <div style={S.sideCard}>
              <p style={S.sideCardTitle}>LINK STATUS</p>
              <div style={{ display:"flex", flexDirection:"column", gap:10 }}>
                {[
                  { label:"Facebook",   value:facebook,    icon:<Facebook  size={13} strokeWidth={1.5}/> },
                  { label:"Instagram",  value:instagram,   icon:<Instagram size={13} strokeWidth={1.5}/> },
                  { label:"Twitter",    value:twitter,     icon:<Twitter   size={13} strokeWidth={1.5}/> },
                  { label:"YouTube",    value:youtube,     icon:<Youtube   size={13} strokeWidth={1.5}/> },
                  { label:"App Store",  value:appStoreId,  icon:<Smartphone size={13} strokeWidth={1.5}/> },
                  { label:"Play Store", value:playStoreId, icon:<Play      size={13} strokeWidth={1.5}/> },
                ].map(item => (
                  <div key={item.label} style={{ display:"flex", alignItems:"center", gap:10 }}>
                    <div style={{
                      width:28, height:28, display:"flex", alignItems:"center",
                      justifyContent:"center", flexShrink:0,
                      border:`1px solid ${item.value.trim() ? C.ink : C.border}`,
                      color:item.value.trim() ? C.ink : C.inkLight,
                      background:item.value.trim() ? "rgba(0,0,0,0.03)" : "transparent",
                      transition:"all 0.2s",
                    }}>
                      {item.icon}
                    </div>
                    <div style={{ flex:1, minWidth:0 }}>
                      <div style={{ fontSize:10, fontWeight:500, color:item.value.trim()?C.ink:C.inkLight }}>
                        {item.label}
                      </div>
                      {item.value.trim() ? (
                        <div style={{ fontSize:9, color:C.inkLight, overflow:"hidden", textOverflow:"ellipsis", whiteSpace:"nowrap", fontFamily:"'Courier New',monospace", marginTop:1 }}>
                          {item.value.length > 30 ? item.value.slice(0,30)+"…" : item.value}
                        </div>
                      ) : (
                        <div style={{ fontSize:9, color:C.border }}>Not set</div>
                      )}
                    </div>
                    <div style={{
                      width:6, height:6, borderRadius:"50%", flexShrink:0,
                      background:item.value.trim() ? C.success : C.border,
                      transition:"background 0.2s",
                    }}/>
                  </div>
                ))}
              </div>
            </div>

            {/* Progress bar */}
            <div style={S.sideCard}>
              <p style={S.sideCardTitle}>COMPLETION</p>
              <div style={{ display:"flex", alignItems:"center", gap:10, marginBottom:6 }}>
                <div style={{ flex:1, height:3, background:C.border, overflow:"hidden" }}>
                  <motion.div
                    initial={{ width:0 }}
                    animate={{ width:`${Math.round(filledCount/6*100)}%` }}
                    transition={{ duration:0.5, ease:[0.4,0,0.2,1] }}
                    style={{ height:"100%", background:C.ink }}
                  />
                </div>
                <span style={{ fontSize:10, color:C.inkMid, fontFamily:C.sans, flexShrink:0 }}>
                  {Math.round(filledCount/6*100)}%
                </span>
              </div>
              <p style={{ fontSize:10, color:C.inkLight }}>
                {filledCount === 6
                  ? "All links configured ✓"
                  : `${6 - filledCount} link${6-filledCount!==1?"s":""} remaining`}
              </p>
            </div>

            {/* Note */}
            <div style={{ padding:"14px 0 0" }}>
              <div style={{ height:1, background:C.border, marginBottom:12 }}/>
              <p style={{ fontSize:10, color:C.inkLight, lineHeight:1.7 }}>
                Links are used across your site footer, share widgets, and app download banners. Save after each update.
              </p>
            </div>

          </div>

        </div>
      </div>
    </div>
  );
};

export default SocialHandles;

/* ─────────────────────────────────────────────────────────
   STYLES
───────────────────────────────────────────────────────── */
const S: { [key: string]: React.CSSProperties } = {
  titleStrip: { padding:"20px 20px 0" },
  eye:        { fontSize:9, letterSpacing:"0.3em", color:C.inkMid, fontFamily:C.sans, marginBottom:6 },
  title:      { fontFamily:C.serif, fontSize:"clamp(26px,4vw,42px)" as any, fontWeight:600, color:C.ink, lineHeight:1, margin:0, letterSpacing:"-0.01em" },
  countPill:  { fontSize:10, letterSpacing:"0.14em", color:C.inkMid, fontFamily:C.sans, paddingBottom:4 },

  body: { maxWidth:1160, margin:"0 auto", padding:"28px 20px 60px" },

  sectionHead:  { display:"flex", alignItems:"center", gap:14, marginBottom:14 },
  sectionTitle: { fontFamily:C.serif, fontSize:20, fontWeight:600, color:C.ink, margin:0 },

  saveBtn: {
    display:"inline-flex", alignItems:"center", justifyContent:"center",
    padding:"13px 32px", fontSize:10, fontWeight:700,
    letterSpacing:"0.22em", fontFamily:C.sans,
    alignSelf:"flex-start" as const, transition:"all 0.3s",
    width:"100%",
  },

  sidebar: { position:"sticky" as const, top:20, display:"flex", flexDirection:"column", gap:16 },
  sideCard: { border:`1px solid ${C.border}`, padding:"16px 16px" },
  sideCardTitle: { fontSize:9, letterSpacing:"0.25em", color:C.ink, fontFamily:C.sans, fontWeight:700, marginBottom:14 },
};

const SH_CSS = `
  * { box-sizing: border-box; }
  ::-webkit-scrollbar { width: 0; }
  input::placeholder { color: #a3a3a3; font-size: 11px; }

  @keyframes sh-spin { from{transform:rotate(0deg);} to{transform:rotate(360deg);} }
  .sh-spin { animation: sh-spin 1s linear infinite; }

  .sh-layout {
    display: grid;
    grid-template-columns: 1fr 280px;
    gap: 40px;
    align-items: start;
  }

  @media (max-width: 720px) {
    .sh-layout {
      grid-template-columns: 1fr !important;
      gap: 28px !important;
    }
    .sh-layout > div:last-child {
      position: relative !important;
      top: 0 !important;
    }
  }
`;